'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { use, useMemo, useState } from 'react';
import { VaultInfoHeader } from 'src/components/Vaults/VaultPage/VaultHeader';
import { VaultMainContent } from 'src/components/Vaults/VaultMainContent';
import { PageRoute, VAULT_VIEW_TAB_OPTIONS } from 'src/constants/constants';
import { UiVaults } from 'src/constants/vaults';
import { SubscribedVaultProvider } from 'src/providers/vaults/SubscribedVaultProvider';
import { useVaultOnChainData } from 'src/hooks/query/vaults/base/useVaultOnChainData';
import { useVaultDepositorAccounts } from 'src/hooks/query/vaults/base/useVaultDepositorAccounts';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { notify } from 'src/utils/notifications';
import Button from 'src/components/primitives/Button/Button';
import { VaultDepositWithdrawForm } from 'src/components/Vaults/VaultMainContent/VaultDepositWithdrawForm';
import { UiVaultConfig } from 'src/@types/vaults';

interface VaultPageContentProps {
	uiVaultConfig: UiVaultConfig;
}

const VaultPageContent = ({ uiVaultConfig }: VaultPageContentProps) => {
	const searchParams = useSearchParams();
	const tab = searchParams?.get('tab') as keyof typeof VAULT_VIEW_TAB_OPTIONS;

	const [showMobileVaultForm, setShowMobileVaultForm] = useState(false);
	const [formType, setFormType] = useState<'deposit' | 'withdraw'>('deposit');

	const handleCloseForm = () => {
		setShowMobileVaultForm(false);
	};

	const handleOpenDepositForm = () => {
		setFormType('deposit');
		setShowMobileVaultForm(true);
	};

	const handleOpenWithdrawForm = () => {
		setFormType('withdraw');
		setShowMobileVaultForm(true);
	};

	const vaultPubkey = uiVaultConfig.vaultPubkeyString;
	const { data: onChainData } = useVaultOnChainData();
	const { data: depositorAccounts, isLoading: isDepositorLoading } =
		useVaultDepositorAccounts();
	const vaultAccountData = onChainData?.[vaultPubkey]?.vaultAccountData;
	const vaultDepositorAccountData =
		depositorAccounts?.[vaultPubkey]?.vaultDepositor;
	const isVaultDepositorLoaded = !isDepositorLoading;

	const isDeprecating = uiVaultConfig.isDeprecating;

	return (
		<>
			<div className="relative h-full w-full px-md text-text-default sm:px-8">
				{/** Main Content */}
				<div className="relative z-10 flex w-full justify-center md:px-4">
					<div className="flex w-full max-w-[1328px] flex-col gap-y-lg pt-9 pb-20 sm:gap-2xl sm:pb-6">
						<VaultInfoHeader uiVaultConfig={uiVaultConfig} />
						<VaultMainContent uiVaultConfig={uiVaultConfig} initialTab={tab} />
					</div>

					{/* Mobile Deposit Form Triggers */}
					<div className="fixed inset-x-0 bottom-0 z-20 flex gap-2 border-t border-stroke-divider p-4 backdrop-blur-md sm:hidden">
						{!isDeprecating && (
							<Button
								size="lg"
								onClick={handleOpenDepositForm}
								className="w-full"
								variant="primary"
							>
								Deposit
							</Button>
						)}
						<Button
							size="lg"
							onClick={handleOpenWithdrawForm}
							className="w-full"
							variant="secondary"
						>
							Withdraw
						</Button>
					</div>
				</div>
			</div>
			{showMobileVaultForm && (
				<div
					className={`fixed inset-x-0 bottom-0 z-150 rounded-sm border border-stroke-divider bg-container-bg transition-transform duration-300 ease-in-out ${
						showMobileVaultForm ? 'translate-y-0' : 'translate-y-full'
					}`}
				>
					<div className="h-full overflow-y-auto">
						<VaultDepositWithdrawForm
							type={formType}
							uiVaultConfig={uiVaultConfig}
							vaultAccountData={vaultAccountData}
							vaultDepositorAccountData={vaultDepositorAccountData}
							isVaultDepositorLoaded={isVaultDepositorLoaded}
							handleCloseForm={handleCloseForm}
						/>
					</div>
				</div>
			)}
		</>
	);
};

export default function VaultPage(props: {
	params: Promise<{ vaultAddress: string }>;
}) {
	const { vaultAddress } = use(props.params);
	const searchParams = useSearchParams();
	const forceExternal = searchParams?.get('forceExternal') === 'true';

	const router = useRouter();
	const areVaultConfigsLoaded = useVaultsStore((s) => s.areVaultConfigsLoaded);

	const uiVaultConfig = useMemo(() => {
		if (!areVaultConfigsLoaded) return undefined;
		return UiVaults.getVaultConfig(vaultAddress);
	}, [vaultAddress, areVaultConfigsLoaded]);

	if (!uiVaultConfig) return null;

	// prevent user interaction with external vaults - mainly for legal reasons
	if (!!uiVaultConfig.externalUiLink && !forceExternal) {
		notify({
			type: 'error',
			message: 'Vault not found',
		});
		router.replace(`/${PageRoute.vaults}`);

		return null;
	}

	return (
		<SubscribedVaultProvider vaultPubkey={uiVaultConfig.vaultPubkeyString}>
			<VaultPageContent uiVaultConfig={uiVaultConfig} />
		</SubscribedVaultProvider>
	);
}
